import { useEffect, useMemo, useRef, useState } from 'react';
import { buildBybitPublicWebSocketUrl, resolveBybitInstrument } from '../api/bybitClient';

export const BYBIT_ORDERBOOK_STREAM_DEPTH = 50;
export const BYBIT_ORDERBOOK_VISIBLE_LEVELS = 3;
export const BYBIT_WS_PING_INTERVAL_MS = 20000;
export const DEFAULT_SAFE_SLIPPAGE_BPS = 15;

const BYBIT_WS_RECONNECT_DELAY_MS = 1500;
const CATEGORY_LABELS = {
  linear: 'Linear',
  spot: 'Spot',
  inverse: 'Inverse'
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const uniqueSymbols = (symbols = []) => {
  const seen = new Set();
  const normalized = [];

  symbols.forEach((symbol) => {
    const next = String(symbol || '').trim().toUpperCase();
    if (!next || seen.has(next)) return;
    seen.add(next);
    normalized.push(next);
  });

  return normalized;
};

const getPrecisionFromStep = (step, fallback = 2) => {
  const value = String(step ?? '').trim();
  if (!value) return fallback;

  if (value.includes('e-')) {
    const exponent = Number(value.split('e-')[1]);
    return Number.isFinite(exponent) ? exponent : fallback;
  }

  const fraction = value.split('.')[1];
  if (!fraction) return 0;

  return fraction.replace(/0+$/, '').length || 0;
};

const roundDownToStep = (value, step) => {
  const numericValue = toNumber(value);
  const numericStep = toNumber(step);

  if (!numericStep) return numericValue;

  return Math.floor((numericValue + Number.EPSILON) / numericStep) * numericStep;
};

const sortLevels = (levels, side, limit) => {
  const sorted = [...levels].sort((left, right) => {
    const leftPrice = toNumber(left[0]);
    const rightPrice = toNumber(right[0]);

    return side === 'bids' ? rightPrice - leftPrice : leftPrice - rightPrice;
  });

  return sorted.slice(0, limit);
};

const applyDeltaLevels = (currentLevels = [], deltaLevels = [], side, limit) => {
  const nextLevels = new Map(currentLevels.map(([price, size]) => [String(price), String(size)]));

  deltaLevels.forEach(([price, size]) => {
    const priceKey = String(price);
    const sizeValue = String(size);

    if (toNumber(sizeValue) === 0) {
      nextLevels.delete(priceKey);
      return;
    }

    nextLevels.set(priceKey, sizeValue);
  });

  return sortLevels([...nextLevels.entries()], side, limit);
};

const decorateLevels = (levels = []) => {
  let cumulativeQty = 0;
  let cumulativeNotional = 0;

  return levels
    .map(([priceRaw, sizeRaw]) => {
      const price = toNumber(priceRaw);
      const size = toNumber(sizeRaw);

      if (!price || !size) return null;

      const notional = price * size;
      cumulativeQty += size;
      cumulativeNotional += notional;

      return {
        price,
        size,
        notional,
        cumulativeQty,
        cumulativeNotional
      };
    })
    .filter(Boolean);
};

const getAdverseTickAllowance = (bestPrice, maxMoveRatio, tickSize) => {
  const normalizedBestPrice = toNumber(bestPrice);
  const normalizedTickSize = toNumber(tickSize);

  if (!normalizedBestPrice || !normalizedTickSize || !maxMoveRatio) {
    return 0;
  }

  return Math.max(Math.ceil(((normalizedBestPrice * maxMoveRatio) - Number.EPSILON) / normalizedTickSize), 0);
};

const getDepthWithinBudget = (levels, bestPrice, maxMoveRatio, direction, tickSize) => {
  const normalizedBestPrice = toNumber(bestPrice);
  const normalizedTickSize = toNumber(tickSize);
  const adverseTicks = getAdverseTickAllowance(normalizedBestPrice, maxMoveRatio, normalizedTickSize);
  const budgetPrice = normalizedTickSize && adverseTicks > 0
    ? direction === 'buy'
      ? normalizedBestPrice + (adverseTicks * normalizedTickSize)
      : Math.max(normalizedBestPrice - (adverseTicks * normalizedTickSize), 0)
    : direction === 'buy'
      ? normalizedBestPrice * (1 + maxMoveRatio)
      : normalizedBestPrice * (1 - maxMoveRatio);
  const tolerance = normalizedTickSize ? normalizedTickSize / 1000 : normalizedBestPrice * 1e-12;

  let qty = 0;
  let notional = 0;

  for (const level of levels) {
    const inRange = direction === 'buy'
      ? level.price <= (budgetPrice + tolerance)
      : level.price >= (budgetPrice - tolerance);

    if (!inRange) break;

    qty += level.size;
    notional += level.notional;
  }

  return { qty, notional };
};

const getStableNotionalStep = (value) => {
  const notional = toNumber(value);

  if (notional >= 50000) return 1000;
  if (notional >= 10000) return 500;
  if (notional >= 2500) return 100;
  if (notional >= 1000) return 50;
  if (notional >= 250) return 25;
  if (notional >= 100) return 10;
  if (notional >= 25) return 5;
  if (notional >= 5) return 1;
  if (notional >= 1) return 0.25;

  return 0.05;
};

const stabilizeNotional = (value) => {
  const notional = toNumber(value);
  if (!notional) return 0;

  const rounded = roundDownToStep(notional, getStableNotionalStep(notional));
  return rounded > 0 ? rounded : notional;
};

const estimateExecutionImpact = (levels, bestPrice, targetNotional, direction) => {
  const normalizedBestPrice = toNumber(bestPrice);
  const normalizedTargetNotional = toNumber(targetNotional);

  if (!normalizedBestPrice || !normalizedTargetNotional) {
    return {
      targetNotional: normalizedTargetNotional,
      targetQty: 0,
      filledQty: 0,
      executedNotional: 0,
      avgPrice: 0,
      slippagePct: null,
      fillRatio: 0,
      hasFullFill: false
    };
  }

  const targetQty = normalizedTargetNotional / normalizedBestPrice;
  let remainingQty = targetQty;
  let filledQty = 0;
  let executedNotional = 0;

  for (const level of levels) {
    if (remainingQty <= 0) break;

    const fillQty = Math.min(level.size, remainingQty);
    if (!fillQty) continue;

    filledQty += fillQty;
    executedNotional += fillQty * level.price;
    remainingQty -= fillQty;
  }

  const tolerance = Math.max(targetQty * 1e-8, 1e-12);
  const hasFullFill = remainingQty <= tolerance;
  const avgPrice = filledQty ? executedNotional / filledQty : 0;
  const slippageRatio = avgPrice
    ? direction === 'buy'
      ? (avgPrice - normalizedBestPrice) / normalizedBestPrice
      : (normalizedBestPrice - avgPrice) / normalizedBestPrice
    : 0;

  return {
    targetNotional: normalizedTargetNotional,
    targetQty,
    filledQty,
    executedNotional,
    avgPrice,
    slippagePct: filledQty ? Math.max(slippageRatio, 0) * 100 : null,
    fillRatio: targetQty ? Math.min(filledQty / targetQty, 1) : 0,
    hasFullFill
  };
};

const normalizeStreamPayload = (message) => {
  const data = message?.data || {};

  return {
    symbol: String(data.s || '').toUpperCase(),
    asks: Array.isArray(data.a) ? data.a : [],
    bids: Array.isArray(data.b) ? data.b : [],
    timestamp: Number(message?.ts || message?.cts || Date.now()),
    updateId: Number(data.u || 0),
    sequence: Number(data.seq || 0)
  };
};

const buildOrderbookView = ({ snapshot, instrument, category, strategyEntrySize, slippageBps }) => {
  const asks = decorateLevels(snapshot.asks);
  const bids = decorateLevels(snapshot.bids);

  const bestAsk = asks[0]?.price || 0;
  const bestBid = bids[0]?.price || 0;
  const midPrice = bestAsk && bestBid ? (bestAsk + bestBid) / 2 : 0;
  const spread = bestAsk && bestBid ? bestAsk - bestBid : 0;
  const spreadPct = midPrice ? (spread / midPrice) * 100 : 0;

  const tickSize = instrument?.priceFilter?.tickSize || instrument?.priceScale || '0.01';
  const qtyStep = instrument?.lotSizeFilter?.qtyStep || instrument?.lotSizeFilter?.basePrecision || '0.0001';
  const pricePrecision = getPrecisionFromStep(tickSize, 4);
  const qtyPrecision = getPrecisionFromStep(qtyStep, 4);
  const slippageRatio = slippageBps / 10000;
  const maxRecommendedSlippagePct = slippageBps / 100;
  const normalizedTickSize = toNumber(tickSize);
  const adverseTickPct = bestBid && normalizedTickSize
    ? (normalizedTickSize / bestBid) * 100
    : bestAsk && normalizedTickSize
      ? (normalizedTickSize / bestAsk) * 100
      : 0;
  const maxRecommendedSlippagePctWithTick = maxRecommendedSlippagePct + adverseTickPct;

  const buyDepthWithinBudget = getDepthWithinBudget(asks, bestAsk, slippageRatio, 'buy', tickSize);
  const sellDepthWithinBudget = getDepthWithinBudget(bids, bestBid, slippageRatio, 'sell', tickSize);
  const safePositionNotional = Math.min(
    buyDepthWithinBudget.notional || 0,
    sellDepthWithinBudget.notional || 0
  );
  const safePositionQty = midPrice
    ? roundDownToStep(safePositionNotional / midPrice, qtyStep)
    : 0;
  const stableOptimalPositionNotional = stabilizeNotional(safePositionNotional);
  const stableOptimalPositionQty = midPrice
    ? roundDownToStep(stableOptimalPositionNotional / midPrice, qtyStep)
    : 0;
  const topLevelNotional = Math.min(asks[0]?.notional || 0, bids[0]?.notional || 0);
  const topLevelQty = Math.min(asks[0]?.size || 0, bids[0]?.size || 0);
  const spreadTicks = toNumber(tickSize) ? spread / toNumber(tickSize) : 0;
  const coverageRatio = strategyEntrySize > 0 ? safePositionNotional / strategyEntrySize : null;
  const buyEntryImpact = strategyEntrySize > 0
    ? estimateExecutionImpact(asks, bestAsk, strategyEntrySize, 'buy')
    : null;
  const sellEntryImpact = strategyEntrySize > 0
    ? estimateExecutionImpact(bids, bestBid, strategyEntrySize, 'sell')
    : null;
  const hasEntryLiquidity = strategyEntrySize > 0
    ? Boolean(buyEntryImpact?.hasFullFill && sellEntryImpact?.hasFullFill)
    : null;
  const worstEntryImpactPct = strategyEntrySize > 0
    ? hasEntryLiquidity
      ? Math.max(buyEntryImpact?.slippagePct || 0, sellEntryImpact?.slippagePct || 0)
      : null
    : null;
  const isEntryRecommended = strategyEntrySize > 0
    ? Boolean(hasEntryLiquidity && worstEntryImpactPct !== null && worstEntryImpactPct <= maxRecommendedSlippagePctWithTick)
    : null;
  const pressureRatio = strategyEntrySize > 0 && safePositionNotional > 0
    ? strategyEntrySize / safePositionNotional
    : null;

  return {
    status: 'ready',
    symbol: snapshot.symbol,
    category,
    marketLabel: CATEGORY_LABELS[category] || category,
    timestamp: snapshot.timestamp,
    updateId: snapshot.updateId,
    sequence: snapshot.sequence,
    instrument,
    baseCoin: instrument?.baseCoin || '',
    quoteCoin: instrument?.quoteCoin || 'USDT',
    pricePrecision,
    qtyPrecision,
    tickSize: toNumber(tickSize),
    qtyStep: toNumber(qtyStep),
    bestAsk,
    bestBid,
    midPrice,
    spread,
    spreadPct,
    spreadTicks,
    topLevelNotional,
    topLevelQty,
    maxRecommendedSlippagePct,
    maxRecommendedSlippagePctWithTick,
    safePositionNotional,
    safePositionQty,
    stableOptimalPositionNotional,
    stableOptimalPositionQty,
    buyDepthWithinBudget,
    sellDepthWithinBudget,
    buyEntryImpact,
    sellEntryImpact,
    hasEntryLiquidity,
    worstEntryImpactPct,
    isEntryRecommended,
    strategyEntrySize,
    coverageRatio,
    pressureRatio,
    asks,
    bids,
    rawAsks: snapshot.asks,
    rawBids: snapshot.bids
  };
};

const pickMapEntries = (source, keys) => {
  return keys.reduce((acc, key) => {
    if (source[key]) acc[key] = source[key];
    return acc;
  }, {});
};

const groupReadySymbolsByCategory = (symbols, instrumentStates) => {
  return symbols.reduce((acc, symbol) => {
    const instrumentState = instrumentStates[symbol];
    if (instrumentState?.status !== 'ready') return acc;

    const category = instrumentState.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push(symbol);
    return acc;
  }, {});
};

export const useBybitOrderbooks = ({
  symbols = [],
  strategyEntrySize = 0,
  depth = BYBIT_ORDERBOOK_STREAM_DEPTH,
  slippageBps = DEFAULT_SAFE_SLIPPAGE_BPS
} = {}) => {
  const normalizedSymbols = useMemo(() => uniqueSymbols(symbols), [symbols]);
  const symbolKey = normalizedSymbols.join('|');

  const instrumentCacheRef = useRef({});
  const [instrumentStates, setInstrumentStates] = useState({});
  const [orderbooks, setOrderbooks] = useState({});
  const [socketStates, setSocketStates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!normalizedSymbols.length) {
      setInstrumentStates({});
      setOrderbooks({});
      setSocketStates({});
      setIsLoading(false);
      setError('');
      setLastUpdated(null);
      return undefined;
    }

    let isActive = true;
    const controller = new AbortController();
    const missingSymbols = normalizedSymbols.filter((symbol) => !instrumentCacheRef.current[symbol]);

    setInstrumentStates(pickMapEntries(instrumentCacheRef.current, normalizedSymbols));

    if (!missingSymbols.length) {
      return () => controller.abort();
    }

    const loadInstruments = async () => {
      const results = await Promise.allSettled(
        missingSymbols.map((symbol) => resolveBybitInstrument(symbol, { signal: controller.signal }))
      );

      if (!isActive) return;

      missingSymbols.forEach((symbol, index) => {
        const result = results[index];
        instrumentCacheRef.current[symbol] = result.status === 'fulfilled'
          ? {
              status: 'ready',
              symbol,
              category: result.value.category,
              instrument: result.value.instrument
            }
          : {
              status: 'error',
              symbol,
              error: result.reason?.message || `Failed to resolve ${symbol}`
            };
      });

      setInstrumentStates(pickMapEntries(instrumentCacheRef.current, normalizedSymbols));
    };

    loadInstruments().catch((requestError) => {
      if (!isActive || requestError?.name === 'AbortError') return;
      setError(requestError.message || 'Failed to resolve Bybit instruments');
    });

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [normalizedSymbols, symbolKey]);

  useEffect(() => {
    if (!normalizedSymbols.length) return undefined;

    const groupedSymbols = groupReadySymbolsByCategory(normalizedSymbols, instrumentStates);
    const categories = Object.keys(groupedSymbols);

    setOrderbooks((prev) => pickMapEntries(prev, normalizedSymbols));
    setSocketStates((prev) => pickMapEntries(prev, categories));

    if (!categories.length) return undefined;

    let isActive = true;
    const sockets = new Map();
    const pingTimers = new Map();
    const reconnectTimers = new Map();

    setIsLoading(true);

    const clearSocketResources = (category) => {
      const pingTimer = pingTimers.get(category);
      if (pingTimer) {
        window.clearInterval(pingTimer);
        pingTimers.delete(category);
      }

      const reconnectTimer = reconnectTimers.get(category);
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
        reconnectTimers.delete(category);
      }

      const socket = sockets.get(category);
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.close();
        }
        sockets.delete(category);
      }
    };

    const connectCategory = (category, categorySymbols, attempt = 'initial') => {
      if (!isActive || !categorySymbols.length) return;

      setSocketStates((prev) => ({
        ...prev,
        [category]: attempt === 'initial' ? 'connecting' : 'reconnecting'
      }));

      const socket = new WebSocket(buildBybitPublicWebSocketUrl(category));
      sockets.set(category, socket);

      socket.onopen = () => {
        if (!isActive) return;

        setSocketStates((prev) => ({ ...prev, [category]: 'live' }));
        setError('');

        socket.send(
          JSON.stringify({
            op: 'subscribe',
            args: categorySymbols.map((symbol) => `orderbook.${depth}.${symbol}`)
          })
        );

        const pingTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ op: 'ping' }));
          }
        }, BYBIT_WS_PING_INTERVAL_MS);

        pingTimers.set(category, pingTimer);
      };

      socket.onmessage = (event) => {
        if (!isActive) return;

        let message;
        try {
          message = JSON.parse(event.data);
        } catch (parseError) {
          return;
        }

        if (message?.success === false) {
          setError(message.ret_msg || `Bybit WebSocket request failed (${category})`);
          setSocketStates((prev) => ({ ...prev, [category]: 'error' }));
          return;
        }

        if (!message?.topic?.startsWith('orderbook.') || !message?.data?.s) {
          return;
        }

        const streamData = normalizeStreamPayload(message);
        if (!streamData.symbol) return;

        const instrumentState = instrumentStates[streamData.symbol];
        if (!instrumentState?.instrument) return;

        setOrderbooks((prev) => {
          const current = prev[streamData.symbol];

          if (message.type !== 'snapshot' && !current?.rawAsks && !current?.rawBids) {
            return prev;
          }

          const mergedSnapshot = message.type === 'snapshot' || !current
            ? streamData
            : {
                ...streamData,
                asks: applyDeltaLevels(current.rawAsks, streamData.asks, 'asks', depth),
                bids: applyDeltaLevels(current.rawBids, streamData.bids, 'bids', depth)
              };

          return {
            ...prev,
            [streamData.symbol]: buildOrderbookView({
              snapshot: mergedSnapshot,
              instrument: instrumentState.instrument,
              category: instrumentState.category,
              strategyEntrySize: toNumber(strategyEntrySize),
              slippageBps
            })
          };
        });

        setLastUpdated(Date.now());
        setIsLoading(false);
      };

      socket.onerror = () => {
        if (!isActive) return;
        setSocketStates((prev) => ({ ...prev, [category]: 'error' }));
      };

      socket.onclose = () => {
        clearSocketResources(category);

        if (!isActive) return;

        setSocketStates((prev) => ({ ...prev, [category]: 'reconnecting' }));

        const reconnectTimer = window.setTimeout(() => {
          connectCategory(category, categorySymbols, 'reconnect');
        }, BYBIT_WS_RECONNECT_DELAY_MS);

        reconnectTimers.set(category, reconnectTimer);
      };
    };

    categories.forEach((category) => {
      connectCategory(category, groupedSymbols[category], 'initial');
    });

    return () => {
      isActive = false;
      categories.forEach((category) => clearSocketResources(category));
    };
  }, [normalizedSymbols, instrumentStates, strategyEntrySize, depth, slippageBps, symbolKey]);

  const items = useMemo(() => {
    return normalizedSymbols.map((symbol) => {
      if (orderbooks[symbol]) return orderbooks[symbol];
      if (instrumentStates[symbol]?.status === 'error') {
        return {
          status: 'error',
          symbol,
          error: instrumentStates[symbol].error
        };
      }

      return {
        status: 'loading',
        symbol,
        category: instrumentStates[symbol]?.category,
        marketLabel: instrumentStates[symbol]?.category
          ? (CATEGORY_LABELS[instrumentStates[symbol].category] || instrumentStates[symbol].category)
          : null
      };
    });
  }, [normalizedSymbols, orderbooks, instrumentStates]);

  const connectionState = useMemo(() => {
    const categories = Object.keys(groupReadySymbolsByCategory(normalizedSymbols, instrumentStates));
    const states = categories.map((category) => socketStates[category]).filter(Boolean);

    if (!states.length) return 'idle';
    if (states.some((state) => state === 'reconnecting')) return 'reconnecting';
    if (states.every((state) => state === 'live')) return 'live';
    if (states.some((state) => state === 'live')) return 'syncing';
    if (states.some((state) => state === 'connecting')) return 'connecting';
    if (states.some((state) => state === 'error')) return 'error';
    return 'idle';
  }, [normalizedSymbols, instrumentStates, socketStates]);

  return {
    items,
    isLoading,
    isRefreshing: connectionState === 'reconnecting',
    lastUpdated,
    error,
    connectionState
  };
};
